'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input } from '@venienergygroup/shared-components';
import { useTranslations } from 'next-intl';
import { SubmitHandler, useForm } from 'react-hook-form';
import z from 'zod';

const createEmailSchema = (t: ReturnType<typeof useTranslations>) =>
  z.object({ email: z.email(t('otp.email.validationInvalid')) });

type EmailFormValues = z.infer<ReturnType<typeof createEmailSchema>>;

interface OTPEmailRequestProps {
  onEmailSubmitted: (email: string) => void;
  isLoading?: boolean;
}

export const OTPEmailRequest = ({ onEmailSubmitted, isLoading = false }: OTPEmailRequestProps) => {
  const t = useTranslations('LoginPage');

  const emailSchema = createEmailSchema(t);
  const { register, handleSubmit, formState } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
  });

  const { isSubmitting, errors } = formState;

  // Requesting the OTP is the parent's responsibility so it survives this component unmounting
  const onSubmit: SubmitHandler<EmailFormValues> = (data) => {
    onEmailSubmitted(data.email);
  };

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
      <div className="flex flex-col gap-y-2">
        <label htmlFor="email" className="block text-sm font-medium">
          {t('otp.email.label')}
        </label>
        <Input
          {...register('email')}
          id="email"
          type="email"
          placeholder={t('otp.email.placeholder')}
          disabled={isSubmitting || isLoading}
          aria-invalid={!!errors.email}
          autoComplete="email"
        />
        {errors.email && <p className="text-sm text-error-700">{errors.email.message}</p>}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting || isLoading}
        className="w-full disabled:cursor-not-allowed"
      >
        {isSubmitting ? t('otp.email.sendButtonLoading') : t('otp.email.sendButton')}
      </Button>
    </form>
  );
};
